import React, { useState, useEffect, useRef } from "react";
import { Settings, LogOut, HelpCircle } from "lucide-react";
import Flag from "react-world-flags";
import logo from "../../assets/newLogo.png";
import down from "../../assets/Down.svg";
import { Link } from "react-router-dom";
import axios from "axios";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase";

const HeaderDashboard = ({ toggleSidebar, burgerMenuRef }) => {
  const [userInitials, setUserInitials] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState({
    code: "RU",
    label: "РУС",
  });
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const languageMenuRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const savedUserProfile = JSON.parse(localStorage.getItem("user_profile"));
    if (savedUserProfile) {
      setUserInitials(savedUserProfile.initials || "");
      setProfileImage(savedUserProfile.profileImage || null);
    } else {
      const fetchUserData = async () => {
        try {
          const response = await axios.get(
            `https://us-central1-extent-protocol-a.cloudfunctions.net/getUserByToken?token=${token}`
          );

          const userData = response.data.user;
          if (!userData) {
            return;
          }

          const { name = "", surname = "" } = userData;
          let computedInitials = "";
          if (name.length > 0) {
            computedInitials += name[0];
          }
          if (surname.length > 0) {
            computedInitials += surname[0];
          }
          computedInitials = computedInitials.toUpperCase();
          setUserInitials(computedInitials);

          let userProfile = { initials: computedInitials };

          try {
            const imageRef = ref(storage, `pictures/${token}.png`);
            const url = await getDownloadURL(imageRef);
            userProfile.profileImage = url;
            setProfileImage(url);
          } catch (err) {}
        } catch (error) {}
      };

      fetchUserData();
    }
  }, []);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("auth_password");
    localStorage.removeItem("user_profile");

    const authURL =
      window.location.hostname === "localhost"
        ? "http://localhost:3000/auth/login"
        : "https://sawir.app/auth/login";

    window.location.href = authURL;
  };

  const toggleLanguageMenu = () => {
    setIsLanguageMenuOpen(!isLanguageMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target)
      ) {
        setIsLanguageMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  return (
    <nav className="bg-white shadow-sm border-b border-gray-100 px-3 sm:px-4 py-2 sm:py-3 z-40 h-12 sm:h-16 fixed w-full top-0">
      <div className="flex items-center justify-between h-full">
        {/* Header Logo Section */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          <div
            className="flex flex-col space-y-0.5 sm:space-y-1 cursor-pointer p-1.5 sm:p-2 rounded-md hover:bg-gray-100 transition-colors"
            onClick={toggleSidebar}
            ref={burgerMenuRef}
          >
            <div className="w-4 sm:w-5 h-0.5 bg-gray-600 rounded"></div>
            <div className="w-4 sm:w-5 h-0.5 bg-gray-600 rounded"></div>
            <div className="w-4 sm:w-5 h-0.5 bg-gray-600 rounded"></div>
          </div>

          <img
            src={logo}
            alt="Sawir School Logo"
            className="h-6 sm:h-8 md:h-10 cursor-pointer"
            onClick={() => (window.location.href = "/")}
          />
        </div>

        {/* Header Actions Section */}
        <div className="flex items-center space-x-1 sm:space-x-3">
          {/* Language Selector */}
          <div
            className="hidden xs:flex relative items-center cursor-pointer p-1.5 sm:p-2 rounded-md hover:bg-gray-50 transition-colors"
            onClick={toggleLanguageMenu}
            ref={languageMenuRef}
          >
            <Flag
              code={selectedLanguage.code}
              style={{
                width: "16px",
                height: "12px",
                marginRight: "4px",
              }}
              className="sm:w-6 sm:h-4 sm:mr-2"
            />
            <span className="text-xs sm:text-sm font-medium text-gray-700 hidden sm:inline">
              {selectedLanguage.label}
            </span>

            {isLanguageMenuOpen && (
              <ul className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-md shadow-lg py-2 min-w-28 sm:min-w-32 z-50">
                {[
                  { code: "KZ", label: "ҚАЗ" },
                  { code: "RU", label: "РУС" },
                  { code: "GB", label: "ENG" },
                ]
                  .filter((lang) => lang.code !== selectedLanguage.code)
                  .map((lang) => (
                    <li
                      key={lang.code}
                      onClick={() => setSelectedLanguage(lang)}
                      className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-green-50 cursor-pointer transition-colors"
                    >
                      <Flag
                        code={lang.code}
                        style={{
                          width: "16px",
                          height: "12px",
                          marginRight: "4px",
                        }}
                        className="sm:w-6 sm:h-4 sm:mr-2"
                      />
                      <span className="text-xs sm:text-sm text-gray-700">{lang.label}</span>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <div 
              className="flex items-center cursor-pointer p-1 sm:p-2 rounded-md hover:bg-gray-50 transition-colors" 
              onClick={toggleUserMenu}
            >
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover mr-1 sm:mr-2"
                  onError={() => setProfileImage(null)}
                />
              ) : (
                <span className="w-6 h-6 sm:w-8 sm:h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-medium mr-1 sm:mr-2">
                  {userInitials}
                </span>
              )}
              <img src={down} alt="dropdown-icon" className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
            </div>

            {isUserMenuOpen && (
              <ul className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-md shadow-lg py-2 min-w-44 sm:min-w-48 z-50">
                <li>
                  <Link 
                    to="/profile" 
                    className="flex items-center px-3 sm:px-4 py-2 hover:bg-green-50 text-gray-700 transition-colors text-decoration-none"
                  >
                    <Settings className="w-4 h-4 mr-2 sm:mr-3 text-gray-500" />
                    Профиль
                  </Link>
                </li>

                <li className="flex items-center px-3 sm:px-4 py-2 hover:bg-green-50 text-gray-700 cursor-pointer transition-colors">
                  <HelpCircle className="w-4 h-4 mr-2 sm:mr-3 text-gray-500" />
                  FAQ
                </li>
                
                <hr className="my-2 border-gray-100" />
                
                <li>
                  <div 
                    onClick={logout} 
                    className="flex items-center px-3 sm:px-4 py-2 hover:bg-red-50 text-gray-700 cursor-pointer transition-colors"
                  >
                    <LogOut className="w-4 h-4 mr-2 sm:mr-3 text-gray-500" />
                    Выйти
                  </div>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default HeaderDashboard;